import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Trash2, CheckCircle, AlertCircle } from 'lucide-react';

/**
 * /delete-account — PUBLIC account-deletion request page (no login needed).
 *
 * Required by Google Play's account-deletion policy: apps with account
 * creation must provide a web resource where users can request deletion
 * without reinstalling/logging in. This is the URL declared in the Play
 * Console data-safety form. Backed by POST /api/public/delete-account →
 * data_subject_requests (fulfilled manually within 30 days).
 */
export default function DeleteAccountPage() {
  const [email, setEmail] = useState('');
  const [details, setDetails] = useState('');
  const [state, setState] = useState('idle'); // idle | submitting | done | error
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setState('submitting');
    setError('');
    try {
      await axios.post('/public/delete-account', { email, details: details || undefined });
      setState('done');
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong — please try again later.');
      setState('error');
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--surface)' }}>
      <div style={{ maxWidth: 560, margin: '0 auto', padding: 'calc(40px + var(--sat)) 20px 80px' }}>
        <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 24 }}>
          <img src="/marakin-logo-charcoal.png" alt="Marakin" style={{ height: 22 }} />
        </Link>

        <h1 style={{ fontSize: 26, marginBottom: 10 }}>Delete your Marakin account</h1>
        <p style={{ fontSize: 15, lineHeight: 1.6, color: 'var(--text-secondary)', marginBottom: 16 }}>
          You can ask us to delete your account and the personal data that goes with it —
          profile, club memberships, event responses, QR redemption history and consent records.
          Some records may be kept where your club has a legitimate need (for example match
          results you appear in) or where the law requires it. Full details in our{' '}
          <Link to="/privacy" style={{ color: 'var(--green)', textDecoration: 'underline' }}>privacy policy</Link>.
        </p>
        <p style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--text-secondary)', marginBottom: 24 }}>
          <strong>Fastest route if you still have the app:</strong> My account → Privacy &amp; data →
          Request deletion. Otherwise use this form — we'll confirm by email and complete the
          deletion within 30 days.
        </p>

        {state === 'done' ? (
          <div className="card" style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <CheckCircle size={22} style={{ color: 'var(--success)', flexShrink: 0, marginTop: 2 }} />
            <div>
              <p style={{ fontWeight: 700, marginBottom: 4 }}>Request received</p>
              <p style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
                If an account exists for that email address, we'll confirm by email and complete
                the deletion within 30 days.
              </p>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="card">
            <div className="form-group" style={{ marginBottom: 14 }}>
              <label className="label">Account email</label>
              <input
                className="input"
                type="email"
                required
                placeholder="you@example.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 18 }}>
              <label className="label">Anything we should know <span style={{ textTransform: 'none', fontWeight: 500 }}>(optional)</span></label>
              <textarea
                className="input"
                rows={3}
                placeholder="e.g. please also remove my child's profile"
                value={details}
                onChange={e => setDetails(e.target.value)}
              />
            </div>
            {error && (
              <p style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--danger)', fontSize: 13, marginBottom: 12 }}>
                <AlertCircle size={14} /> {error}
              </p>
            )}
            <button type="submit" className="btn btn-primary" disabled={state === 'submitting'} style={{ width: '100%', justifyContent: 'center' }}>
              <Trash2 size={15} /> {state === 'submitting' ? 'Sending…' : 'Request account deletion'}
            </button>
          </form>
        )}

        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 28 }}>
          Marakin · <Link to="/privacy" style={{ textDecoration: 'underline' }}>Privacy policy</Link>
        </p>
      </div>
    </div>
  );
}
